import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Import images
import speedBreaker from '../../assets/products/speed-breaker.jpg';
import safetyCones from '../../assets/products/safety-cones.jpg';
import safetyJacket from '../../assets/products/safety-jacket.jpg';
import radiumTape from '../../assets/products/radium-tape.jpg';
import co2Extinguisher from '../../assets/products/co2-extinguisher.jpg';
import abcExtinguisher from '../../assets/products/abc-extinguisher.jpg';
import fireBlanket from '../../assets/products/fire-blanket.jpg';
import fireBucket from '../../assets/products/fire-bucket.jpeg';

const RoadSafety = () => {
  const safetyEquipment = [
    {
      title: "Speed Breaker",
      description: "A speed breaker is a road safety device designed to slow down vehicles and reduce the risk of accidents in high-traffic or sensitive areas such as industrial zones, construction sites, parking lots, and highways.",
      color: "text-yellow-600",
      icon: "🚧",
      image: speedBreaker,
      category: "Road Safety",
      features: [
        "Durable construction",
        "High visibility markings",
        "Easy installation",
        "Weather resistant",
        "Various sizes available"
      ],
      applications: [
        "Industrial zones",
        "Construction sites",
        "Parking lots",
        "School zones",
        "High-traffic areas"
      ]
    },
    {
      title: "Safety Cones",
      description: "Safety cones, also known as traffic cones or road cones, are cone-shaped markers used to manage traffic, indicate hazards, and create safe zones in industrial, construction, and road environments.",
      color: "text-orange-600",
      icon: "🔺",
      image: safetyCones,
      category: "Road Safety",
      features: [
        "High visibility colors",
        "Stackable design",
        "UV resistant",
        "Reflective bands",
        "Stable base"
      ],
      applications: [
        "Traffic management",
        "Hazard marking",
        "Construction zones",
        "Parking management",
        "Event organization"
      ]
    },
    {
      title: "Safety Jacket",
      description: "A safety jacket is a high-visibility protective garment designed to enhance worker visibility and safety in low-light or hazardous environments. It is widely used in construction, roadwork, fabrication, warehouses, traffic management, and industrial workplaces.",
      color: "text-green-500",
      icon: "🦺",
      image: safetyJacket,
      category: "Road Safety",
      features: [
        "Reflective strips",
        "Breathable material",
        "Multiple pockets",
        "Adjustable fit",
        "Washable fabric"
      ],
      applications: [
        "Construction work",
        "Road maintenance",
        "Traffic control",
        "Night operations",
        "Emergency response"
      ]
    },
    {
      title: "Radium Tape",
      description: "Radium tape, also known as glow-in-the-dark tape or reflective tape, is a safety marking tape designed to improve visibility in low-light or dark conditions. It is commonly used in industrial safety, road safety, signage, vehicles, and hazardous areas.",
      color: "text-blue-400",
      icon: "📍",
      image: radiumTape,
      category: "Road Safety",
      features: [
        "Photoluminescent properties",
        "Weather resistant",
        "Strong adhesive",
        "Long-lasting glow",
        "Easy application"
      ],
      applications: [
        "Emergency exits",
        "Vehicle marking",
        "Safety signage",
        "Equipment marking",
        "Stair edges"
      ]
    },
    {
      title: "CO₂ Fire Extinguisher",
      description: "A CO₂ (Carbon Dioxide) fire extinguisher is a firefighting device used to suppress fires by displacing oxygen and cooling down the flames. It is highly effective for electrical and Class B (flammable liquid) fires.",
      color: "text-red-600",
      icon: "🧯",
      image: co2Extinguisher,
      category: "Fire Safety",
      features: [
        "Non-conductive",
        "No residue",
        "Quick discharge",
        "Effective cooling",
        "Pressure gauge"
      ],
      applications: [
        "Electrical fires",
        "Liquid fires",
        "Server rooms",
        "Laboratories",
        "Industrial equipment"
      ]
    },
    {
      title: "ABC Fire Extinguisher",
      description: "An ABC fire extinguisher is a multipurpose extinguisher that can combat Class A, B, and C fires. It is widely used in industries, offices, homes, and commercial spaces due to its versatility and effectiveness.",
      color: "text-red-500",
      icon: "🧯",
      image: abcExtinguisher,
      category: "Fire Safety",
      features: [
        "Multipurpose use",
        "Dry chemical agent",
        "Easy operation",
        "Pressure indicator",
        "Wide coverage"
      ],
      applications: [
        "Office spaces",
        "Industrial areas",
        "Residential buildings",
        "Commercial spaces",
        "Educational institutions"
      ]
    },
    {
      title: "Fire Blanket",
      description: "A fire blanket is a safety device designed to extinguish small fires by smothering the flames and cutting off the oxygen supply. It is widely used in homes, kitchens, laboratories, industrial workplaces, and welding areas for fire protection.",
      color: "text-gray-600",
      icon: "🛡️",
      image: fireBlanket,
      category: "Fire Safety",
      features: [
        "Fire resistant material",
        "Quick deployment",
        "Compact storage",
        "Reusable design",
        "Easy to use"
      ],
      applications: [
        "Kitchen fires",
        "Laboratory safety",
        "Welding operations",
        "Small fire control",
        "Personal protection"
      ]
    },
    {
      title: "Fire Bucket with Stand",
      description: "A fire bucket with stand is a simple yet effective firefighting tool used for extinguishing small fires, especially in industrial, commercial, and public areas. It is typically filled with sand or water and placed in easily accessible locations.",
      color: "text-red-700",
      icon: "🪣",
      image: fireBucket,
      category: "Fire Safety",
      features: [
        "Durable construction",
        "Stable stand",
        "Weather resistant",
        "High visibility",
        "Easy access"
      ],
      applications: [
        "Industrial areas",
        "Construction sites",
        "Storage facilities",
        "Public spaces",
        "Emergency stations"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-[160px]">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-gray-900 to-[#333333]">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:60px_60px]" />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800/20 via-transparent to-gray-800/20" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold text-white mb-6"
            >
              Road & Fire Safety Equipment
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-white max-w-3xl mx-auto"
            >
              Comprehensive safety solutions for road safety and fire protection
            </motion.p>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1.5, delay: 0.5 }}
              className="mt-16 h-1 max-w-xl mx-auto bg-gradient-to-r from-transparent via-gray-400 to-transparent"
            />
          </div>
        </div>
      </section>

      {/* Category Sections */}
      {["Road Safety", "Fire Safety"].map((category, categoryIdx) => (
        <section key={category} className={`py-20 ${categoryIdx % 2 === 1 ? 'bg-gray-50' : ''}`}>
          <div className="container mx-auto px-4">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-center text-black mb-16"
            >
              {category}
            </motion.h2>
            <div className="grid md:grid-cols-2 gap-8">
              {safetyEquipment
                .filter(item => item.category === category)
                .map((item, idx) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300"
                >
                  {/* Product Image */}
                  <div className="mb-6 overflow-hidden rounded-xl">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-64 object-cover transform hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  <div className="flex items-center mb-6">
                    {/* <span className="text-4xl mr-4">{item.icon}</span> */}
                    <div>
                      <h3 className="text-3xl font-bold text-black mb-2">
                        {item.title}
                      </h3>
                      <div className="w-32 h-1 bg-gray-200"></div>
                    </div>
                  </div>
                  
                  <p className="text-black text-lg mb-6">
                    {item.description}
                  </p>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-xl font-semibold text-black mb-3">
                        Key Features
                      </h4>
                      <ul className="list-disc list-inside space-y-2 text-black">
                        {item.features.map((feature, featureIdx) => (
                          <li key={featureIdx}>{feature}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold text-black mb-3">
                        Applications
                      </h4>
                      <ul className="list-disc list-inside space-y-2 text-black">
                        {item.applications.map((application, appIdx) => (
                          <li key={appIdx}>{application}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Safety Notice Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <h2 className="text-3xl font-bold text-black mb-6">
              Safety Guidelines
            </h2>
            <div className="space-y-4 text-black">
              <p>
                Regular maintenance and proper usage of safety equipment is crucial for ensuring workplace and public safety. 
                Always follow manufacturer guidelines and safety protocols.
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Regularly inspect all safety equipment</li>
                <li>Maintain clear access to fire safety equipment</li>
                <li>Train personnel in proper equipment use</li>
                <li>Keep safety equipment in designated locations</li>
                <li>Replace damaged or expired equipment promptly</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
              Need Safety Equipment?
            </h2>
            <p className="text-black mb-8">
              Our safety experts are here to help you choose the right equipment for your specific requirements
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 text-lg font-medium text-white bg-gray-800 hover:bg-gray-700 rounded-full transition-all duration-300 transform hover:scale-105"
            >
              Contact Our Experts
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoadSafety;